/**
 * Environment configuration with runtime config support
 *
 * Configuration sources (in priority order):
 * 1. Runtime config (window.__RUNTIME_CONFIG__) - injected at container start
 * 2. Build-time env (import.meta.env.VITE_*) - baked in during build
 *
 * This allows changing configuration without rebuilding the Docker image.
 */

// Type for runtime configuration
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      VITE_CDB_TQS_API_URL?: string;
      VITE_BACKEND_API_URL?: string;
      VITE_AZURE_AD_TENANT_ID?: string;
      VITE_AZURE_AD_CLIENT_ID?: string;
      VITE_AZURE_AD_REDIRECT_URI?: string;
      VITE_AZURE_STORAGE_URL?: string;
      VITE_AZURE_STORAGE_CONTAINER?: string;
      VITE_DEBUG_MODE?: string;
      VITE_APP_TITLE?: string;
      VITE_APP_BRANDING?: string;
      VITE_APPINSIGHTS_CONNECTION_STRING?: string;
    };
  }
}

/**
 * Get runtime config value with fallback to build-time env
 * Ignores placeholder values (e.g., "__VITE_BACKEND_API_URL__")
 */
function getRuntimeConfig(key: string): string | undefined {
  const runtimeValue = window.__RUNTIME_CONFIG__?.[key as keyof typeof window.__RUNTIME_CONFIG__];

  // Check if runtime value is valid (not a placeholder)
  if (runtimeValue && !runtimeValue.startsWith('__') && !runtimeValue.endsWith('__')) {
    return runtimeValue;
  }

  // Fallback to build-time environment variable
  return import.meta.env[key] as string | undefined;
}

/**
 * Environment configuration object
 * Use these getters instead of accessing import.meta.env directly
 */
export const env = {
  // Backend URLs
  get cdbTqsApiUrl() {
    return getRuntimeConfig('VITE_CDB_TQS_API_URL') || 'http://localhost:8000';
  },
  get backendApiUrl() {
    return getRuntimeConfig('VITE_BACKEND_API_URL') || 'http://localhost:8001';
  },

  // Azure AD
  get azureAdTenantId() {
    return getRuntimeConfig('VITE_AZURE_AD_TENANT_ID') || '';
  },
  get azureAdClientId() {
    return getRuntimeConfig('VITE_AZURE_AD_CLIENT_ID') || '';
  },
  get azureAdRedirectUri() {
    return getRuntimeConfig('VITE_AZURE_AD_REDIRECT_URI') || 'http://localhost:5174';
  },

  // Realtime notifications (#1090): the BFF hands out the Web PubSub URL.
  get realtimeNegotiateUrl() {
    return `${this.backendApiUrl}/api/v1/realtime/negotiate`;
  },

  // Storage (Azure Blob)
  get azureStorageUrl() {
    return getRuntimeConfig('VITE_AZURE_STORAGE_URL') || '';
  },
  get azureStorageContainer() {
    return getRuntimeConfig('VITE_AZURE_STORAGE_CONTAINER') || 'general';
  },

  // Environment
  get isDevelopment() {
    return import.meta.env.DEV;
  },
  get isProduction() {
    return import.meta.env.PROD;
  },

  // Client Branding
  get appTitle() {
    return getRuntimeConfig('VITE_APP_TITLE') || 'cdbOS';
  },
  get appBranding() {
    return getRuntimeConfig('VITE_APP_BRANDING') || '';
  },

  // Debug Mode
  get debugMode() {
    return getRuntimeConfig('VITE_DEBUG_MODE') === 'true';
  },

  /** One widget bar with edit mode and gallery instead of the Widget Manager (#1822). */

  // Observability (Azure Application Insights — see cdb_control_center#1)
  get appInsightsConnectionString() {
    return getRuntimeConfig('VITE_APPINSIGHTS_CONNECTION_STRING') || '';
  },
  get gitSha() {
    // Build-time only: injected via docker build-arg from CI's github.sha.
    return import.meta.env.VITE_GIT_SHA || '';
  },
  get appVersion() {
    // Build-time only: `YYYY.MM.DD-<sha7>`, derived in vite.config.ts from the
    // build date and VITE_GIT_SHA (#1355). Local builds have no sha.
    return import.meta.env.VITE_APP_VERSION || 'dev';
  },
};

// Log environment info in development (only when debug mode is enabled)
if (env.isDevelopment && env.debugMode) {
  console.log('Environment Configuration:');
  console.log('  Backend API:', env.backendApiUrl);
  console.log('  CDB-TQS API:', env.cdbTqsApiUrl);
  console.log('  Debug Mode:', env.debugMode ? 'Enabled' : 'Disabled');
}
